// src/pages/subscription/SubscriptionChangePlanPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useSubscriptionPlan, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return String(dateStr); }
}

export const SubscriptionChangePlanPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscription();
  const planStore = useSubscriptionPlan();
  const { t } = useI18n();

  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetSubscription(id);
    }
    planStore.ListPlans({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    }).then(() => {
      setPlans(Array.isArray(planStore.items) ? [...planStore.items] : []);
    }).catch(() => {
      // Error handled by store
    });

    return () => {
      store.clearCurrent();
    };
  }, [id]);

  useEffect(() => {
    if (!selectedPlan || !id) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    store.GetSubscriptionPlanChangePreview(id, { new_plan_id: selectedPlan }).then((res: any) => {
      setPreview(res);
    }).catch(() => {
      setPreview(null);
    }).finally(() => {
      setPreviewLoading(false);
    });
  }, [selectedPlan, id]);

  const handleSubmit = async () => {
    if (!id || !selectedPlan) return;
    setSubmitting(true);
    try {
      await store.SetSubscriptionPlanChange(id, {
        new_plan: { id: selectedPlan },
      } as any);
      navigate(`/subscriptions/${id}`);
    } catch {
      // Error handled by store
    } finally {
      setSubmitting(false);
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const currentPlanId = (item as any)?.plan?.entity?.id || (item as any)?.plan?.id || (item as any)?.plan_id;

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">{t('subscription.changePlan')}</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
            {t('subscription.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/subscriptions/${id}`} className="text-muted-foreground hover:text-foreground">
            {id}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('subscription.changePlan')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.currentPlan')}</CardTitle>
          <CardDescription>{t('subscription.currentPlanDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.plan')}</dt>
              <dd className="text-sm text-foreground mt-1">
                {String((item as any)?.plan?.name ?? (item as any)?.plan_id ?? '--')}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.price')}</dt>
              <dd className="text-sm text-foreground mt-1">
                {formatMoney((item as any)?.current_price)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.billingCycle')}</dt>
              <dd className="text-sm text-foreground mt-1">
                {(item as any)?.billing_cycle
                  ? formatLabel((item as any).billing_cycle, 'BILLING_CYCLE_')
                  : '--'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.status')}</dt>
              <dd className="text-sm text-foreground mt-1">
                <Badge variant="primary" size="sm">
                  {(item as any)?.status
                    ? formatLabel((item as any).status, 'SUBSCRIPTION_STATUS_')
                    : '--'}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.availablePlans')}</CardTitle>
          <CardDescription>{t('subscription.selectNewPlan')}</CardDescription>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('subscription.noPlansAvailable')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan: any) => {
                const planId = plan.entity?.id || plan.id;
                const isCurrent = planId === currentPlanId;
                const isSelected = planId === selectedPlan;

                return (
                  <button
                    key={planId}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setSelectedPlan(planId)}
                    className={[
                      'text-left rounded-lg border-2 p-4 transition-all',
                      isCurrent
                        ? 'border-muted bg-muted/50 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'border-primary ring-2 ring-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 cursor-pointer',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        {String(plan.name ?? '--')}
                      </h3>
                      {isCurrent && (
                        <Badge variant="secondary" size="sm">{t('subscription.current')}</Badge>
                      )}
                    </div>
                    <p className="text-xl font-bold text-foreground mb-1">
                      {formatMoney(plan.price)}
                      {plan.billing_cycle && (
                        <span className="text-sm font-normal text-muted-foreground">
                          /{formatLabel(plan.billing_cycle, 'BILLING_CYCLE_').toLowerCase()}
                        </span>
                      )}
                    </p>
                    {plan.description && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                        {String(plan.description)}
                      </p>
                    )}
                    {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {plan.features.slice(0, 5).map((feature: any, idx: number) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-center gap-1">
                            <svg className="h-3 w-3 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {String(feature.name ?? feature)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      {selectedPlan && (
        <Card>
          <CardHeader>
            <CardTitle>{t('subscription.changePreview')}</CardTitle>
            <CardDescription>{t('subscription.changePreviewDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {previewLoading ? (
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : preview ? (
              <dl className="divide-y divide-border">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('subscription.priceDifference')}</dt>
                  <dd className="text-sm text-foreground col-span-2">
                    {formatMoney(preview.price_difference)}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('subscription.effectiveDate')}</dt>
                  <dd className="text-sm text-foreground col-span-2">
                    {formatDate(preview.effective_at ?? preview.effective_date)}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('subscription.prorationAmount')}</dt>
                  <dd className="text-sm text-foreground col-span-2">
                    {formatMoney(preview.proration_amount)}
                  </dd>
                </div>
                {preview.new_price != null && (
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('subscription.newPrice')}</dt>
                    <dd className="text-sm text-foreground col-span-2">
                      {formatMoney(preview.new_price)}
                    </dd>
                  </div>
                )}
                {preview.description && (
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('subscription.details')}</dt>
                    <dd className="text-sm text-foreground col-span-2">
                      {String(preview.description)}
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">{t('subscription.noPreviewAvailable')}</p>
            )}
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <Link to={`/subscriptions/${id}`}>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button
              onClick={handleSubmit}
              disabled={!selectedPlan || submitting}
            >
              {submitting ? t('common.saving') : t('subscription.confirmPlanChange')}
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
});

export default SubscriptionChangePlanPage;
